<template>
    <Container v-if="!isLoading && post">
        <div class="tw-mb-5 tw-flex tw-flex-col tw-items-center tw-gap-3">
            <div class="tw-flex tw-size-[120px] tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-black tw-bg-gray-300">
                <v-avatar
                    class="tw-size-[108px]"
                    rounded="full">
                    <v-img :src="post.authorImageUrl || undefined"></v-img>
                </v-avatar>
            </div>
            <span
                class="tw-mx-auto tw-px-2 tw-py-1 tw-font-title tw-text-sm tw-font-medium"
                v-html="post.authorName">
            </span>
            <h1
                class="tw-text-center tw-font-title tw-text-2xl tw-font-bold tw-text-dark"
                v-html="post.title"></h1>
            <!-- relativeDate -->
            <div class="tw-text-center tw-text-sm tw-text-gray">
                {{ relativeDate }}
            </div>
        </div>
        <article class="tw-mb-10">
            <div
                class="post-content tw-text-base"
                v-html="formatedContent"></div>
        </article>
    </Container>
    <Loader v-else />
</template>

<script setup lang="ts">
    import Loader from './loader.vue';
    import { getOpinionById, getRelativeDate } from '@/composables/usePost';
    import { APP_NAME } from '@/configs/app';
    import type { IOpinion } from '@/contentTypes';
    import { useAppStore } from '@/store/app';

    const appStore = useAppStore();
    const $route = useRoute();
    const isLoading = ref(false);

    const post = ref<IOpinion | null>(null);

    const postId = computed(() => {
        const id = $route.params.id;
        if (typeof id === 'string') {
            return parseInt(id, 10);
        }
        return null;
    });

    const relativeDate = computed(() => {
        return getRelativeDate(post.value?.date);
    });

    const formatedContent = computed(() => {
        // Use replace with regex for compatibility with older JS versions
        return post.value?.contentHtml ? post.value.contentHtml.replace(/(\r\n|\n|\r)/g, '<br>') : '';
    });

    async function init() {
        isLoading.value = true;
        try {
            if (postId.value !== null) {
                const _opinion = await getOpinionById(postId.value);
                if (_opinion) {
                    post.value = _opinion;
                    if (post.value) {
                        // set the document title as the opinion title
                        document.title = `${post.value.title} - ${APP_NAME}`;
                    }
                } else {
                    console.error('Opinion not found');
                }
                appStore.setOpenPostUrl(post.value?.link || '');
            }
        } catch (error) {
            console.error('Error fetching opinion:', error);
        } finally {
            if (post.value) {
                // set the document title as the opinion title
                document.title = `${post.value.title} - ${APP_NAME}`;
            }
            isLoading.value = false;
        }
    }

    async function initInstagramEmbeds() {
        await nextTick();
        const instagramEmbeds = document.querySelectorAll('iframe[src*="instagram.com"]');
        instagramEmbeds.forEach((embed) => {
            if (embed instanceof HTMLIFrameElement) {
                const src = embed.getAttribute('src');
                if (src && !src.includes('embed')) {
                    const newSrc = src.replace(/\/p\//, '/embed/p/');
                    embed.setAttribute('src', newSrc);
                }
            }
        });
        // Reinitialize the Instagram embeds
        if (window.instgrm) {
            window.instgrm.Embeds.process();
        } else {
            console.warn('Instagram embeds library not found. Ensure it is loaded before this script.');
            // Optionally, you could load the library here
        }
    }

    const stopPostIdWatcher = watch(
        postId,
        (newId, oldId) => {
            if (newId !== oldId && $route.name === 'OpinionPage') {
                init();
            }
        },
        { immediate: true },
    );

    const stopContentWatcher = watch(
        () => post.value?.contentHtml,
        (newContent) => {
            if (newContent) {
                initInstagramEmbeds();
            }
        },
        { immediate: true },
    );

    function handleWindowFocus() {
        if (postId.value !== null && !isLoading.value) {
            init();
        }
    }

    onMounted(() => {
        window.addEventListener('focus', handleWindowFocus);
    });

    onUnmounted(() => {
        stopPostIdWatcher();
        stopContentWatcher();
        window.removeEventListener('focus', handleWindowFocus);
    });

    // init();
</script>

<style lang="scss">
    .post-content {
        --wp--preset--color--black: #000000;
        --wp--preset--color--cyan-bluish-gray: #abb8c3;
        --wp--preset--color--white: #ffffff;
        --wp--preset--color--pale-pink: #f78da7;
        --wp--preset--color--vivid-red: #cf2e2e;
        --wp--preset--color--luminous-vivid-orange: #ff6900;
        --wp--preset--color--luminous-vivid-amber: #fcb900;
        --wp--preset--color--light-green-cyan: #7bdcb5;
        --wp--preset--color--vivid-green-cyan: #00d084;
        --wp--preset--color--pale-cyan-blue: #8ed1fc;
        --wp--preset--color--vivid-cyan-blue: #0693e3;
        --wp--preset--color--vivid-purple: #9b51e0;

        figure {
            @apply tw-mb-[1em];
        }

        figure,
        iframe {
            @apply tw-w-full tw-max-w-full tw-text-center;
            margin-inline: auto !important;
        }

        img {
            border: none;
            border-radius: 0;
            box-shadow: none;
            height: auto;
            max-width: 100%;
        }

        :before {
            box-sizing: border-box;
        }

        iframe[src*='youtube.com'] {
            /* Your styles here */
            aspect-ratio: 16 / 9;
            border: none;
            border-radius: 0;
            box-shadow: none;
            height: 100%;
            max-width: 100%;
            width: 100%;
        }

        figure {
            &.wp-block-image {
                border-radius: 10px;
                overflow: hidden;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                flex-grow: 1;
                justify-content: center;
                max-width: 100%;
                position: relative;
                width: 100%;

                figcaption:before {
                    backdrop-filter: blur(3px);
                    content: '';
                    height: 100%;
                    mask-image: linear-gradient(0deg, #000 20%, #0000);
                    max-height: 100%;
                    bottom: 0;
                    left: 0;
                    position: absolute;
                    right: 0;
                    z-index: -1;
                }

                img {
                    flex: 1 0 0%;
                    height: 100%;
                    object-fit: cover;
                    width: 100%;
                }

                figcaption {
                    background: linear-gradient(0deg, #0006, #0000);
                    box-sizing: border-box;
                    color: #fff;
                    font-size: 13px;
                    margin: 0;
                    overflow: auto;
                    padding: 1em;
                    scrollbar-color: #0000 #0000;
                    scrollbar-gutter: stable both-edges;
                    scrollbar-width: thin;
                    text-align: center;
                    text-shadow: 0 0 1.5px #000;
                    will-change: transform;
                    bottom: 0;
                    left: 0;
                    max-height: 100%;
                    position: absolute;
                    right: 0;
                    line-height: 1.25;
                }
            }
            &.wp-block-gallery {
                align-items: normal;
                display: flex;
                flex-wrap: wrap;
                gap: 1rem;

                figure.wp-block-image:not(#individual-image) {
                    width: calc(100% - var(--wp--style--unstable-gallery-gap, 16px) * 0.66667);
                }

                figure.wp-block-image:not(#individual-image):first-child:nth-last-child(2),
                figure.wp-block-image:not(#individual-image):first-child:nth-last-child(2) ~ figure.wp-block-image:not(#individual-image) {
                    width: calc(100% - var(--wp--style--unstable-gallery-gap, 16px) * 0.5);
                }
            }
        }

        .has-black-color {
            color: var(--wp--preset--color--black) !important;
        }

        .has-cyan-bluish-gray-color {
            color: var(--wp--preset--color--cyan-bluish-gray) !important;
        }

        .has-white-color {
            color: var(--wp--preset--color--white) !important;
        }

        .has-pale-pink-color {
            color: var(--wp--preset--color--pale-pink) !important;
        }

        .has-vivid-red-color {
            color: var(--wp--preset--color--vivid-red) !important;
        }

        .has-luminous-vivid-orange-color {
            color: var(--wp--preset--color--luminous-vivid-orange) !important;
        }

        .has-luminous-vivid-amber-color {
            color: var(--wp--preset--color--luminous-vivid-amber) !important;
        }

        .has-light-green-cyan-color {
            color: var(--wp--preset--color--light-green-cyan) !important;
        }

        .has-vivid-green-cyan-color {
            color: var(--wp--preset--color--vivid-green-cyan) !important;
        }

        .has-pale-cyan-blue-color {
            color: var(--wp--preset--color--pale-cyan-blue) !important;
        }

        .has-vivid-cyan-blue-color {
            color: var(--wp--preset--color--vivid-cyan-blue) !important;
        }

        .has-vivid-purple-color {
            color: var(--wp--preset--color--vivid-purple) !important;
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            margin-block-start: 0.5rem;
            margin-block-end: 1rem;
            font-family: inherit;
            font-weight: 500;
            line-height: 1.2;
            color: inherit;
        }

        h2 {
            font-size: 2rem;
        }

        p {
            margin-block-start: 0;
            margin-block-end: 0.9rem;
        }

        a[href] {
            @apply tw-text-motor;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }
</style>
